// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorProductFormPage
// =============================================================================

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { ProductForm } from '@/features/vendor/components/ProductForm';
import {
  useVendorProduct,
  useCreateProduct,
  useUpdateProduct,
  useVendorCategories,
} from '@/features/vendor/hooks/useVendorProducts';
import type { ProductFormData } from '@/features/vendor/types/vendorTypes';

export function VendorProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const { data: product, isLoading: productLoading } = useVendorProduct(id || '');
  const { data: categories } = useVendorCategories();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();

  const handleSubmit = async (data: ProductFormData) => {
    if (isEditing && id) {
      await updateProduct.mutateAsync({ id, data });
    } else {
      await createProduct.mutateAsync(data);
    }
    navigate('/vendor/products');
  };

  if (isEditing && productLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <VendorHeader
        title={isEditing ? 'Edit Product' : 'Add New Product'}
        subtitle={isEditing ? `Editing ${product?.name}` : 'Create a new product listing'}
        actions={
          <button
            onClick={() => navigate('/vendor/products')}
            className="flex items-center gap-2 px-4 py-2 text-navy font-medium hover:bg-cream-dark rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Products
          </button>
        }
      />

      <div className="p-6 max-w-4xl">
        <ProductForm
          initialData={product}
          categories={categories || []}
          onSubmit={handleSubmit}
          isSubmitting={createProduct.isPending || updateProduct.isPending}
        />
      </div>
    </div>
  );
}

export default VendorProductFormPage;


